/**
 * Booking-availability domain types.
 *
 * This slice owns the three pieces of data the booking calendar needs before a
 * customer or coach can pick a new slot when rescheduling a session:
 *
 *   1. which weekdays the coach takes bookings on,
 *   2. which date ranges the coach has blocked out,
 *   3. which times are open on a chosen date.
 *
 * Read-only data, so it is modelled as plain domain interfaces (no entity
 * class), following the past-calls and payouts slices.
 *
 * Pure TypeScript - no React imports.
 */

import {Weekday} from "./weekdays";

/** An inclusive date range the coach is unavailable, as `YYYY-MM-DD` strings. */
export interface UnavailableDateRange {
  from: string;
  to: string;
}

/**
 * Whether a time can be booked.
 *
 * v1 only ever returned bookable times; v2 returns every time in the coach's
 * working window with a status, so anything the API does not report as
 * available is normalized to "unavailable" (Tolerant Reader).
 */
export type BookingTimeSlotStatus = "available" | "unavailable";

export interface BookingTimeSlot {
  /** Display time in the customer's timezone, e.g. "09:00 AM". */
  time: string;
  status: BookingTimeSlotStatus;
}

export interface BookingTimeSlots {
  /** `YYYY-MM-DD` the slots belong to. Empty on v1, which does not return it. */
  date: string;
  /** Empty on v1, which does not echo the timezones back. */
  coachTimezone: string;
  customerTimezone: string;
  slots: BookingTimeSlot[];
}

/** The times a customer can actually pick, in the order the API returned them. */
export const availableTimes = (slots: readonly BookingTimeSlot[]): string[] =>
  slots.filter((slot) => slot.status === "available").map((slot) => slot.time);

/**
 * Identifies one day on one offering.
 *
 * `month` is 1-based and `date` is the day of the month, matching both APIs.
 */
export interface BookingTimeSlotsQuery {
  mentorId: number;
  serviceId: number;
  date: number;
  month: number;
  year: number;
  day: Weekday;
  userTimeZone: string;
  /** v1 only. v2 resolves the coach's timezone server-side. */
  mentorTimeZone?: string;
}

export interface BookingTimeSlotsRepository {
  getTimeSlots(query: BookingTimeSlotsQuery): Promise<BookingTimeSlots>;
}

/** The weekdays and blocked-out ranges that shape the calendar grid. */
export interface BookingSchedule {
  availableWeekdays: Weekday[];
  unavailableDates: UnavailableDateRange[];
}

/**
 * Reads the calendar-shaping data for one offering.
 *
 * v2 only: v1 has no standalone endpoints for this, it embeds `days` and
 * `unavailableDates` in the booking payload the reschedule drawer already
 * fetches. See `useBookingSchedule` for how that fallback is wired.
 */
export interface BookingScheduleRepository {
  getAvailableWeekdays(query: {mentorId: number; serviceId: number}): Promise<Weekday[]>;
  getUnavailableDates(query: {mentorId: number}): Promise<UnavailableDateRange[]>;
}
